import { Trash } from '@phosphor-icons/react';
import { cloneElement, ReactElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Confirmation } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { useCanManageParts, useCorporatePartPriceActions } from 'corporate/hooks';
import { ICorporatePartPriceListItem } from 'corporate/types';
import { Color, Variant } from 'theme/enums';

interface IProps {
  prices: ICorporatePartPriceListItem[];
  buttonElement?: ReactElement;
  buttonLabel?: string;
  onRemoveSuccess?: () => void;
  disabled?: boolean;
  notificationPosition?: NotificationPosition;
}

export const RemoveCorporatePartPriceButton = ({
  prices,
  buttonElement,
  buttonLabel,
  onRemoveSuccess,
  disabled = false,
  notificationPosition,
}: IProps) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const { t } = useTranslation();
  const canManageParts = useCanManageParts();

  const { removeCorporatePartPrice } = useCorporatePartPriceActions({
    selectionCount: prices.length,
    position: notificationPosition,
  });

  const onRemove = useCallback(async () => {
    setIsConfirmationOpen(false);

    const ids = prices.map(({ id }) => id);

    const { success } = await removeCorporatePartPrice({ ids });

    if (!success) {
      return;
    }

    onRemoveSuccess?.();
  }, [prices, removeCorporatePartPrice, onRemoveSuccess]);

  const button = useMemo(() => {
    if (buttonElement) {
      return cloneElement(buttonElement, {
        onClick: disabled ? undefined : () => setIsConfirmationOpen(true),
      });
    }

    return (
      <Button
        text={buttonLabel ?? t('buttonLabelRemove')}
        color={Color.DANGER}
        variant={Variant.SOFT}
        startDecorator={<Trash />}
        onClick={() => setIsConfirmationOpen(true)}
        disabled={disabled}
      />
    );
  }, [buttonElement, buttonLabel, disabled, t]);

  if (!canManageParts) {
    return null;
  }

  return (
    <>
      {button}

      <Confirmation
        isOpen={isConfirmationOpen}
        title={t('titleRemovePartPrices', { count: prices.length })}
        message={t('messageConfirmRemovePartPrices', { count: prices.length })}
        confirmBtnLabel={t('buttonLabelRemove')}
        confirmBtnColor={Color.DANGER}
        onConfirm={onRemove}
        onCancel={() => setIsConfirmationOpen(false)}
      />
    </>
  );
};
